'use client';

import Link from 'next/link';

import { Button } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { StakeEvent } from '@flipster/utils/segment/events/stake';

import { useLaunchpoolById } from '../_hooks/use-launchpool-by-id';

interface Props {
  launchpoolId: number;
  onClose: () => void;
}

const ConvertInfoModal = ({ launchpoolId, onClose }: Props) => {
  const currentLaunchpool = useLaunchpoolById(launchpoolId);

  const conversionTooltip =
    currentLaunchpool?.status === 'FINISHED'
      ? currentLaunchpool?.conversionTooltip
      : currentLaunchpool?.autoConversionTooltip;
  const isOpenExplainDefault = conversionTooltip?.explanations.length === 1;

  return (
    <Modal size='small'>
      <Modal.Close onClose={onClose} />
      <Modal.Header>
        <Modal.Title
          label={
            <p className={css({ textStyle: 'Title/S' })}>
              <span className={css({ color: 'content.default.level-4' })}>{conversionTooltip?.title} </span>
              <span className={css({ color: 'content.state.positive' })}>{conversionTooltip?.highlightedTitle}</span>
            </p>
          }
        />
      </Modal.Header>
      <Modal.Content css={{ whiteSpace: 'pre-line', pt: '8px!', pb: '16px!' }}>
        {conversionTooltip?.content && (
          <p className={css({ textStyle: 'Text/M/Regular', mb: 16 })}>{conversionTooltip?.content}</p>
        )}
        <ul className={flex({ direction: 'column', gap: 8 })}>
          {conversionTooltip?.explanations.map((ex) => {
            return (
              <li
                key={ex.title}
                className={css({
                  p: 12,
                  color: 'content.default.level-4',
                  bgColor: 'background.default.level-2',
                  borderRadius: 8,
                })}
              >
                <details open={isOpenExplainDefault}>
                  <summary className={flex({ justify: 'space-between', align: 'center', cursor: 'pointer' })}>
                    <span className={css({ textStyle: 'Text/M/Strong' })}>{ex.title}</span>
                    <ChevronDownIcon
                      color='#B4B4B4'
                      size={20}
                      className={css({ '[open] &': { transform: 'rotate(180deg)' } })}
                    />
                  </summary>
                  <p className={css({ textStyle: 'Text/M/Regular', mt: 8 })}>{ex.description}</p>
                  {ex.additionalLink && (
                    <Link
                      href={ex.additionalLink}
                      className={css({
                        display: 'block',
                        textStyle: 'Text/M/Underline',
                        color: 'content.default.level-3',
                        mt: 16,
                      })}
                      onClick={() => {
                        segment.track(StakeEvent.StakeGoToEarnClick, { button_text: ex.additionalLinkTitle });
                        onClose();
                      }}
                    >
                      {ex.additionalLinkTitle}
                    </Link>
                  )}
                </details>
              </li>
            );
          })}
        </ul>
      </Modal.Content>
      <footer className={flex({ justify: 'center', pt: 24, pb: 32 })}>
        <Button size='large' onClick={onClose} label='OK' css={{ w: 154 }} />
      </footer>
    </Modal>
  );
};

export default ConvertInfoModal;
